import { Immutable } from "immer";
import React from "react";

import type { FeaturesBlock as Value } from "@reactivated";

import { concatClassNames } from "@thelabnyc/thelabui/src/utils/styles";

import { getImageSrc } from "../../utils/streamfieldContent";
import { WistiaBackgroundVideo } from "../WistiaBackgroundVideo";

type IProps = Immutable<{
    value: Value;
}>;

/**
 * Faithful port of the legacy server-rendered
 * ``tsicommon_cms/blocks/features-block.html`` (the PDP "Features" block).
 *
 * The ``id="pdp-features"`` is load-bearing: the configurator upsell tooltip
 * scrolls to / anchors on this id, so it MUST be preserved verbatim. It is
 * emitted unconditionally, so a page with more than one FeaturesBlock repeats
 * it -- invalid HTML, but a DELIBERATE invariant: the anchor
 * (``Configurator``'s ``learnMoreSelector``, default ``"#pdp-features"``) is
 * meant to land on the TOPMOST features section, which is exactly what a
 * duplicate id resolves to. Do not make the id unique per block without
 * re-pointing that selector.
 *
 * Layout branches (mirrors the legacy ``{% if not image or not video %}``):
 *   A. list + photo  -- rendered unless BOTH image AND video are set.
 *   B. collapsed     -- rendered ONLY when BOTH image AND video are set.
 *
 * List numbering (branch A): when ``feature_list_type`` is falsy the legacy
 * ``{% cycle 'A'..'Z' %}`` stamps a letter into ``.pdp-feature__char``; we
 * reproduce it positionally with ``String.fromCharCode(65 + (i % 26))`` so the
 * sequence wraps after Z exactly as the legacy cycle would. When a list type IS
 * set, the char div carries the type class and renders no letter.
 *
 * Image: the legacy ``{% wagtail_image features_image lazy=False %}`` expands
 * (per ``lazy-wagtail-image.html``) to a plain eager ``<img>`` whose ``src`` is
 * the LARGEST rendition. The shared ``getImageSrc`` helper (``../utils``) picks
 * the inlined ``imageData`` data-URI if present, else the widest rendition, else
 * ``image.url`` -- and we use ``default_alt_text`` for ``alt`` (matching the
 * other ported blocks: EverydayOfferGridBlock / MegaHeroBlock).
 *
 * Video: rendered through the shared ``WistiaBackgroundVideo`` component, which
 * owns the Wistia embed from its own post-hydration effect (see that component
 * for the hydration-race rationale).
 */

export const FeaturesBlock = (props: IProps): React.JSX.Element => {
    const v = props.value;
    const features = v.features ?? [];
    const image = v.features_image;
    const video = v.features_video;
    const listType = v.feature_list_type;
    // `ChoiceBlock.to_python()` passes a non-string value through as-is, and
    // `concatClassNames` calls `.split` on anything truthy -- coerce so an
    // unexpected shape degrades to unthemed instead of 500ing the SSR render.
    const theme = typeof v.theme === "string" ? v.theme : "";
    // Legacy: `{% if not features_image or not features_video %}` selects the
    // list+photo branch; the `else` (collapsed) branch fires only when BOTH are
    // set. Phrase it directly to avoid a De Morgan slip.
    const isCollapsed = !!image && !!video;
    return (
        <section
            id="pdp-features"
            className={concatClassNames([
                "pdp-section",
                "pdp-features-block",
                "l-capped-width",
                theme,
            ])}
        >
            <div className="pdp-features-block__header">
                {v.features_title && (
                    <h2
                        className="pdp-features-block__title"
                        dangerouslySetInnerHTML={{
                            __html: v.features_title,
                        }}
                    />
                )}
                {/* Legacy renders this desc div UNCONDITIONALLY (even when the
                    description is empty); not guarded. */}
                <div
                    className="pdp-features-block__desc"
                    dangerouslySetInnerHTML={{
                        __html: v.features_description ?? "",
                    }}
                />
            </div>
            <div className="pdp-section_body pdp-features-block__body">
                <div className="pdp-features-block__content">
                    {isCollapsed ? (
                        // Branch B: collapsed list (both image AND video set).
                        features.length > 0 && (
                            <div className="pdp-features-block__list pdp-features-block__list--collapsed">
                                {features.map((feature, i) => (
                                    // Index key: ListBlock items expose no
                                    // stable id, matching the legacy positional
                                    // `forloop` render.
                                    <div
                                        className="pdp-feature pdp-feature--collapsed"
                                        key={i}
                                    >
                                        <div className="pdp-feature__title pdp-feature__title--collapsed">
                                            <h3 className="pdp-feature__text pdp-feature__text--collapsed">
                                                {feature.title}
                                            </h3>
                                        </div>
                                        <p className="pdp-feature__desc pdp-feature__desc--collapsed responsive-img">
                                            {feature.desc}
                                        </p>
                                    </div>
                                ))}
                            </div>
                        )
                    ) : (
                        // Branch A: list + photo.
                        <>
                            {features.length > 0 && (
                                <div className="pdp-features-block__list">
                                    {features.map((feature, i) => (
                                        <div className="pdp-feature" key={i}>
                                            <div className="pdp-feature__title">
                                                {listType ? (
                                                    <div
                                                        className={concatClassNames(
                                                            [
                                                                "pdp-feature__char",
                                                                listType,
                                                            ],
                                                        )}
                                                    ></div>
                                                ) : (
                                                    <div className="pdp-feature__char">
                                                        {String.fromCharCode(
                                                            65 + (i % 26),
                                                        )}
                                                    </div>
                                                )}
                                                <span className="ada-screenreader-only">
                                                    {feature.title}:{" "}
                                                    {feature.desc}
                                                </span>
                                                <h3 className="pdp-feature__text">
                                                    {feature.title}
                                                </h3>
                                            </div>
                                            <p className="pdp-feature__desc responsive-img">
                                                {feature.desc}
                                            </p>
                                        </div>
                                    ))}
                                </div>
                            )}
                            {/* Legacy renders this container div in branch A
                                unconditionally (empty when neither set). */}
                            <div className="pdp-features-block__photo-container">
                                {video ? (
                                    <WistiaBackgroundVideo videoId={video} />
                                ) : image ? (
                                    <img
                                        alt={image.default_alt_text}
                                        src={getImageSrc(image)}
                                        loading="lazy"
                                        decoding="async"
                                    />
                                ) : null}
                            </div>
                        </>
                    )}
                </div>
            </div>
        </section>
    );
};
